<app>
  <div class="d-flex flex-column min-vh-100">
    <app-header isAuthenticated={ state.isAuthenticated } onlogout={ handleLogout } onshowhelp={ handleShowHelp }
      onadmin={ handleAdminPanel } onshowrfid={ handleShowRfid }></app-header>

    <main class="flex-grow-1">
      <div class="container my-4">
        <login if={ !state.isAuthenticated } onlogin={ handleLogin }></login>

        <!-- Alerta de error de conexión API -->
        <div if={ state.isAuthenticated && state.apiError } class="alert alert-danger alert-dismissible fade show mb-4"
          role="alert">
          <div class="d-flex align-items-center">
            <i class="bi bi-exclamation-triangle-fill me-2" style="font-size: 1.5rem;"></i>
            <div>
              <h5 class="alert-heading">Error de conexión</h5>
              <p class="mb-0">{ state.apiErrorMessage }</p>
              <div if={ state.apiErrorType==='connection' } class="mt-2">
                <button class="btn btn-sm btn-outline-danger me-2" onclick={ reloadPage }>
                  <i class="bi bi-arrow-clockwise me-1"></i> Recargar página
                </button>
                <button class="btn btn-sm btn-outline-primary" onclick={ retryLoadDevices }>
                  <i class="bi bi-arrow-repeat me-1"></i> Intentar de nuevo
                </button>
              </div>
            </div>
          </div>
          <button type="button" class="btn-close" aria-label="Close" onclick={ dismissApiError }></button>
        </div>

        <!-- Spinner de carga -->
        <div if={ state.isAuthenticated && state.loading } class="text-center my-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2">Cargando dispositivos...</p>
        </div>

        <!-- Gestor de dispositivos (lista y detalle) -->
        <device-manager if={ state.isAuthenticated && !state.loading && !state.apiError && state.view==='devices' }
          devices={ state.devices }
          isAdmin={ isAdmin() }
          onlogout={ handleLogout }
          ondevicesupdated={ handleDevicesUpdated }
        ></device-manager>

        <!-- Mensaje cuando no hay dispositivos pero la conexión es correcta -->
        <div if={ state.isAuthenticated && !state.loading && !state.apiError && state.devices.length===0 &&
          state.view==='devices' } class="alert alert-info">
          <i class="bi bi-info-circle-fill me-2"></i>
          No se encontraron dispositivos asociados a tu cuenta.
        </div>

        <!-- Editor de perfil -->
        <profile-editor if={ state.isAuthenticated && state.view==='profile' } onupdate={ handleProfileUpdate } onback={
          handleBackFromProfile }></profile-editor>

        <!-- Gestión de RFID -->
        <rfid-manager if={ state.isAuthenticated && state.view==='rfid' } onback={ handleBackFromRfid }></rfid-manager>

        <!-- Renderizar el componente FirmwareManager cuando view sea 'firmware' -->
        <firmware-manager if={ state.isAuthenticated && state.view==='firmware' } onback={ handleBackFromAdmin
          }></firmware-manager>

        <!-- Renderizar el componente DeviceAdmin cuando view sea 'device-admin' -->
        <device-admin if={ state.isAuthenticated && state.view==='device-admin' } onback={ handleBackFromAdmin
          }></device-admin>

        <!-- Renderizar la gestión de usuarios cuando view sea 'user-management' -->
        <user-management if={ state.isAuthenticated && state.view==='user-management' } onback={ handleBackFromAdmin
          }></user-management>

        <!-- Renderizar el componente UserAdmin cuando view sea 'user-admin' -->
        <user-admin if={ state.isAuthenticated && state.view==='user-admin' } onback={ handleBackFromUserManagement
          }></user-admin>
      </div>
    </main>

    <app-footer></app-footer>

    <!-- Incluir el componente de ayuda -->
    <help-modal></help-modal>

    <!-- Componente de notificaciones toast -->
    <toast-notifications id="globalToastComponent"></toast-notifications>
  </div>

  <script>
    import Login from './login.riot'
    import DeviceManager from './device-manager.riot'
    import AppHeader from './header.riot'
    import AppFooter from './footer.riot'
    import HelpModal from './help-modal.riot'
    import ProfileEditor from './profile-editor.riot'
    import RfidManager from './rfid-manager.riot'
    import ToastNotifications from './toast-notifications.riot'
    import FirmwareManager from './firmware-manager.riot'
    import DeviceAdmin from './device-admin.riot'
    import UserManagement from './user-management.riot'
    import UserAdmin from './user-admin.riot'
    import { AuthService, DeviceService } from '../services/api-service'
    import { ToastService } from '../services/toast-service'
    import { webSocketService } from '../services/WebSocketService'

    export default {
      components: {
        Login,
        DeviceManager,
        AppHeader,
        AppFooter,
        HelpModal,
        ProfileEditor,
        RfidManager,
        ToastNotifications,
        FirmwareManager,
        DeviceAdmin,
        UserManagement,
        UserAdmin
      },

      onBeforeMount() {
        // Determinar la vista inicial basada en la URL ANTES de montar componentes
        const path = window.location.pathname;
        const isAdmin = AuthService.isAdmin();
        let initialView = 'devices';

        if (path === '/firmware-manager' && isAdmin) {
          initialView = 'firmware';
        } else if (path === '/create-device' && isAdmin) {
          initialView = 'device-admin';
        } else if (path === '/users' && isAdmin) {
          initialView = 'user-management';
        } else if (path === '/create-user' && isAdmin) {
          initialView = 'user-admin';
        } else if (path === '/profile') {
          initialView = 'profile';
        } else if (path === '/rfid-manager') {
          initialView = 'rfid';
        }

        this.state = {
          isAuthenticated: AuthService.isAuthenticated(),
          devices: [],
          loading: false,
          apiError: false,
          apiErrorMessage: '',
          apiErrorType: '', // 'connection', 'auth', 'server', 'format', 'unknown'
          view: initialView // 'devices', 'profile', 'rfid', 'firmware', 'device-admin', 'user-management', 'user-admin'
        }

        window.removeEventListener('auth:error', this.handleAuthError);
        window.removeEventListener('popstate', this.handlePopState);
      },

      async onMounted() {
        window.addEventListener('auth:error', this.handleAuthError);
        window.addEventListener('popstate', this.handlePopState);

        this.ensureWebSocketConnection();

        // Configurar el servicio de toast con la referencia al componente
        // Usamos una forma más segura de acceder al componente mediante su ID
        setTimeout(() => {
          // Obtener el componente por su ID en lugar de usar refs
          const toastComponent = document.getElementById('globalToastComponent');
          if (toastComponent && toastComponent._tag) {
            ToastService.setToastComponent(toastComponent._tag);
          } else {
            console.warn('Toast component not found or not initialized correctly');
          }
        }, 100); // Esperamos un poco más para que el DOM se actualice completamente

        // La vista ya está determinada en onBeforeMount, solo cargar dispositivos si es necesario
        if (this.state.view === 'devices' && this.state.isAuthenticated) {
          await this.loadDevices();
        }
      },

      ensureWebSocketConnection() {
        if (!this.state.isAuthenticated) {
          return;
        }

        const token = AuthService.getToken();
        if (!token) {
          console.warn('[App] No auth token available to connect WebSocket.');
          return;
        }

        webSocketService.connect(token);
      },

      // Manejador del evento popstate
      handlePopState(event) {
        const path = window.location.pathname;
        if (path === '/firmware-manager' && AuthService.isAdmin()) {
          this.update({ view: 'firmware' });
        } else if (path === '/create-device' && AuthService.isAdmin()) {
          this.update({ view: 'device-admin' });
        } else if (path === '/users' && AuthService.isAdmin()) {
          this.update({ view: 'user-management' });
        } else if (path === '/create-user' && AuthService.isAdmin()) {
          this.update({ view: 'user-admin' });
        } else if (path === '/profile') {
          this.update({ view: 'profile' });
        } else if (path === '/rfid-manager') {
          this.update({ view: 'rfid' });
        } else {
          this.update({ view: 'devices' });
          if (this.state.isAuthenticated) {
            this.loadDevices();
          }
        }
      },

      async handleLogin() {
        this.update({
          isAuthenticated: true,
          loading: true,
          apiError: false,
          apiErrorMessage: ''
        })

        await this.loadDevices()

        // Mostrar mensaje de bienvenida con ToastService
        const userData = AuthService.getUserData();
        if (userData && userData.username) {
          ToastService.success(`¡Bienvenido/a de nuevo, ${userData.username}!`);
        } else {
          ToastService.success('¡Sesión iniciada correctamente!');
        }

        // Es importante reconectar aquí si previamente se desconectó
        this.ensureWebSocketConnection();
      },

      handleLogout() {
        // Primero, desconectar el WebSocket
        console.log('[App] handleLogout: Disconnecting WebSocket...');
        webSocketService.disconnect();

        // Luego, limpiar estado y autenticación local
        AuthService.logout()
        this.update({
          isAuthenticated: false,
          devices: [],
          apiError: false,
          apiErrorMessage: '',
          view: 'devices' // Volver a la vista por defecto (que mostrará login)
        })
        // Opcional: Redirigir explícitamente si es necesario
        // window.history.pushState({}, '', '/');
      },

      handleAuthError(event) {
        // Obtener el mensaje de error
        const message = event.detail.message || 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';

        // Actualizar el estado de la alerta
        this.update({
          apiError: true,
          apiErrorMessage: message,
          apiErrorType: 'session_expired'
        });

        // Realizar el logout
        this.handleLogout();

        // Mostrar un toast de error en lugar de un alert
        ToastService.error(message, 5000); // Mayor duración para este mensaje importante
      },

      // Evento disparado cuando se actualiza un dispositivo
      handleDevicesUpdated() {
        // Recargar la lista de dispositivos solo si estamos en la vista de dispositivos
        if (this.state.view === 'devices') {
          this.loadDevices()
        }
      },

      // Métodos para la gestión de errores
      dismissApiError() {
        this.update({
          apiError: false,
          apiErrorMessage: '',
          apiErrorType: ''
        })
      },

      reloadPage() {
        window.location.reload()
      },

      retryLoadDevices() {
        // Solo recargar si estamos en la vista de dispositivos
        if (this.state.view === 'devices') {
          this.loadDevices()
        }
      },

      // Método para mostrar el modal de ayuda usando una referencia global
      handleShowHelp() {
        if (window.helpScheduleModal) {
          window.helpScheduleModal.show();
        }
      },

      // Métodos para navegación al panel de administración
      handleAdminPanel() {
        if (AuthService.isAdmin()) {
          this.update({ view: 'firmware' });
          window.history.pushState({}, '', '/firmware-manager');
        }
      },

      handleDeviceAdmin() {
        if (AuthService.isAdmin()) {
          this.update({ view: 'device-admin' });
          window.history.pushState({}, '', '/create-device');
        }
      },

      handleUserAdmin() {
        if (AuthService.isAdmin()) {
          this.update({ view: 'user-management' });
          window.history.pushState({}, '', '/users');
        }
      },

      handleBackFromUserManagement() {
        this.update({ view: 'user-management' });
        window.history.pushState({}, '', '/users');
      },

      handleBackFromAdmin() {
        this.update({ view: 'devices' });
        window.history.pushState({}, '', '/');
      },

      handleProfileUpdate() {
        // Recargar los datos del usuario después de la actualización
        const userData = AuthService.getUserData();
        if (userData) {
          this.update({
            username: userData.username || '',
            email: userData.email || '',
            roles: userData.roles || []
          });
        }
      },

      isAdmin() {
        return AuthService.isAdmin();
      },

      handleBackFromProfile() {
        this.update({ view: 'devices' });
        window.history.pushState({}, '', '/');
      },

      handleShowRfid() {
        this.update({ view: 'rfid' });
        window.history.pushState({}, '', '/rfid-manager');
      },

      handleBackFromRfid() {
        this.update({ view: 'devices' });
        window.history.pushState({}, '', '/');
      },

      async loadDevices() {
        try {
          this.update({
            loading: true,
            apiError: false,
            apiErrorMessage: ''
          })

          const result = await DeviceService.getDevices()

          if (result.error) {
            // Si hay un error, lo mostramos
            this.update({
              loading: false,
              apiError: true,
              apiErrorMessage: result.error,
              apiErrorType: result.errorType || 'unknown'
            })
          } else {
            // Si todo va bien, actualizamos los dispositivos
            this.update({
              devices: result.devices,
              loading: false,
              apiError: false
            })
          }
        } catch (error) {
          console.error('Error loading devices:', error)
          this.update({
            loading: false,
            apiError: true,
            apiErrorMessage: 'Error inesperado al cargar dispositivos',
            apiErrorType: 'unknown'
          })
        }
      }
    }
  </script>
</app>
